import { useEffect, useState } from "react";
import caretDownIcon from "@assets/icon-caret-down.svg";
import caretUpIcon from "@assets/icon-caret-up.svg";
import { useIsCancelledMount } from "@hooks/useIsCancelledMount";
import axios from "axios";
import dayjs from "dayjs";
import { isEmpty } from "lodash-es";

import { DEFAULT_PERCENTAGE_INFO } from "./constants";
import type {
  ChartCoord,
  StockData,
  StockHistoricalData,
  TimeFrame,
} from "./types";

export const useStockHistoricalData = (code: string) => {
  const [stockHistoricalData, setStockHistoricalData] =
    useState<StockHistoricalData>();

  const cancelledRef = useIsCancelledMount();

  useEffect(() => {
    if (isEmpty(code)) return;

    const callFetchHistoricalStockData = async () => {
      const { data } = await axios.post<StockHistoricalData>(
        "/_plat/api/v1/stock-historical-data",
        { stockCode: code }
      );

      if (!cancelledRef.current && data) {
        setStockHistoricalData(data);
      }
    };

    callFetchHistoricalStockData();
  }, [code, cancelledRef]);

  return stockHistoricalData;
};

export function useStockData(code: string) {
  const [stockData, setStockData] = useState<StockData>();

  useEffect(() => {
    if (isEmpty(code)) return;

    const callFetchStockData = async () => {
      try {
        const response = await axios.post<StockData>(
          "/_plat/api/v1/stock-data",
          { stockCode: code }
        );

        const jsonData = response.data;
        if (jsonData) setStockData(jsonData);
      } catch (error) {
        return;
      }
    };

    callFetchStockData();
  }, [code]);

  return { stockData };
}

export const getPercentageChangeInfo = (
  changes?: number,
  previous?: number
) => {
  if (typeof changes === "undefined" || typeof previous === "undefined") {
    return DEFAULT_PERCENTAGE_INFO;
  }

  const value = (changes / previous) * 100;
  const percentageChange = Math.round((value + Number.EPSILON) * 100) / 100;

  if (percentageChange > 0) {
    return {
      percentageChange,
      percentageChangeColor: "text-green-300",
      percentageChangeSign: "+",
      percentageChangeIcon: caretUpIcon,
      percentageChangeBGColor: "hover:bg-green-300",
    };
  }

  if (percentageChange < 0) {
    return {
      percentageChange,
      percentageChangeColor: "text-red-400",
      percentageChangeSign: "",
      percentageChangeIcon: caretDownIcon,
      percentageChangeBGColor: "hover:bg-red-400",
    };
  }

  return DEFAULT_PERCENTAGE_INFO;
};

export const getTableData = (stockData: StockData) => {
  return [
    { title: "Buying price", value: stockData.BID },
    { title: "Selling price", value: stockData.OFFER },
    { title: "Amount to be purchased", value: stockData.BID_VOLUME },
    { title: "Amount for sale", value: stockData.OFFER_VOLUME },
    { title: "Opening price", value: stockData.OPEN },
    { title: "Previous closing price", value: stockData.PREVIOUS },
    { title: "Today's high", value: stockData.HIGH },
    { title: "Low today", value: stockData.LOW },
    { title: "P/E ratio", value: stockData.PE },
    { title: "Market value (M)", value: stockData.MARKET_CAP },
    { title: "52 weeks high", value: stockData.YEAR_HIGH },
    { title: "52 weeks low", value: stockData.YEAR_LOW },
    { title: "Dividend yield", value: stockData.DIVIDEND_YIELD },
  ];
};

export const filterStockData = function (
  data: StockHistoricalData, // Endpoint returns data in decreasing order i.e. most recent date first
  unit: TimeFrame = "Days"
) {
  const coordsOnChart: ChartCoord[] = [];
  const keys = Object.keys(data);

  if (unit == "Years") {
    for (let i = 0; i < keys.length; i++) {
      const currentItem = data[i];
      const nextItem = data[i + 1];

      if (nextItem && currentItem) {
        const currentDate = dayjs(currentItem["t"]);
        const nextDate = dayjs(nextItem["t"]);
        const isDateInJanuary = currentDate.month() == 0;
        const isFirstDayOfMonth = currentDate.date() < nextDate.date();

        if (isDateInJanuary && isFirstDayOfMonth) {
          coordsOnChart.push({
            x: currentDate.format("MMM DD, YYYY, h:mm:ss a"),
            y: currentItem["y"],
          });
        }
      }
    }
  } else if (unit == "Months") {
    for (let i = 0; i < keys.length; i++) {
      const currentItem = data[i];
      const nextItem = data[i + 1];

      if (nextItem && currentItem) {
        const currentDate = dayjs(currentItem["t"]);
        const nextDate = dayjs(nextItem["t"]);
        const isNextMonthDifferent = currentDate.month() != nextDate.month();

        if (isNextMonthDifferent) {
          coordsOnChart.push({
            x: currentDate.format("MMM DD, YYYY, h:mm:ss a"),
            y: currentItem["y"],
          });
        }
      }
    }
    coordsOnChart.length = Math.min(coordsOnChart.length, 300);
  } else if (unit == "Days") {
    for (let i = 0; i < Math.min(keys.length, 150); i++) {
      const current = data[i];

      if (current) {
        const currDate = dayjs(current["t"]);

        coordsOnChart.push({
          x: currDate.format("MMM DD, YYYY, h:mm:ss a"),
          y: current["y"],
        });
      }
    }
  }

  return coordsOnChart.reverse();
};
